import { observable, computed, action } from 'mobx';
import moment from 'moment';

import GalleryService from 'services/GalleryService';
import messagingService from 'services/MessagingService/MessagingService';
import {
  GET_GALLERIES,
  GET_GALLERIES_RESPONSE,
  GET_GALLERIES_RESPONSE_STATE,
  GET_GALLERIES_DELETE_PHOTO_STATE,
  GET_PHOTOS,
  GET_PHOTOS_RESPONSE,
  GET_PHOTOS_RESPONSE_STATE,
} from 'services/MessagingService/EventNames';
import FanClubStore from 'stores/FanClubStore';
import APIStates from 'enum/APIStates';
import { ENUM_GALLERY_TYPE } from 'enum/images';

import DefaultImageSrc from '../../../../../images/default_gallery_image.png';

/**
 * Keeps all the information necessary for displaying the galleries
 *
 * @class GalleryStore
 */
class GalleryStore {
  /**
   * Creates an instance of GalleryStore.
   * Hooks up to the messaging service and registers events
   * @memberof GalleryStore
   */
  constructor() {
    // [TODO] Initialization should be done at a top level to Solve all dependencies, should be in constructor params
    this.messagingService = messagingService();
    this.messagingService.on(GET_GALLERIES_RESPONSE, this._getGalleriesResponseHandler);
    this.messagingService.on(GET_PHOTOS_RESPONSE, this._getPhotosResponseHandler);
    this.messagingService.on(GET_PHOTOS_RESPONSE_STATE, this._getPhotosResponseState);
    this.messagingService.on(GET_GALLERIES_RESPONSE_STATE, this._getGalleriesResponseState);
    this.messagingService.on(GET_GALLERIES_DELETE_PHOTO_STATE, this._getDeletePhotoState);
  }

  /**
   * Unbind events
   * Should be called by the destructing Profile component where this class is initialised
   *
   * @memberof GalleryStore
   */
  destructor() {
    this.messagingService.off(GET_GALLERIES_RESPONSE, this._getGalleriesResponseHandler);
    this.messagingService.off(GET_PHOTOS_RESPONSE, this._getPhotosResponseHandler);
    this.messagingService.off(GET_PHOTOS_RESPONSE_STATE, this._getPhotosResponseState);
    this.messagingService.off(GET_GALLERIES_RESPONSE_STATE, this._getGalleriesResponseState);
    this.messagingService.off(GET_GALLERIES_DELETE_PHOTO_STATE, this._getDeletePhotoState);
  }

  /**
   * Unaltered gallery API response object
   *
   * @memberof GalleryStore
   */
  @observable galleries = [];
  @observable photos = [];
  @observable photosState = APIStates.INITIAL;
  @observable galleriesState = APIStates.INITIAL;
  @observable deletePhotoState = APIStates.INITIAL;

  /**
   * Private method => Handles EVent bus get galleries response
   *
   * @memberof GalleryStore
   */
  _getGalleriesResponseHandler = (galleries) => {
    this.galleries = galleries;
  };

  _getPhotosResponseHandler = (photos) => {
    this.photos = photos.content;
  };

  _getPhotosResponseState = (photosState) => {
    this.photosState = photosState;
  };

  _getGalleriesResponseState = (galleriesState) => {
    this.galleriesState = galleriesState;
  };

  _getDeletePhotoState = (deletePhotoState) => {
    this.deletePhotoState = deletePhotoState;
  };

  /**
   * Trigger to get galleries => Sets a new message in the event bus
   * @param {string} performer - Performer name.
   * @memberof GalleryStore
   */
  @action getGalleries = (performer) => {
    this.messagingService.emit(GET_GALLERIES, performer);
  };

  @action getPhotos = (username, galleryId) => {
    const data = {
      username,
      galleryId,
    };
    this.messagingService.emit(GET_PHOTOS, data);
  };

  @action deletePhoto = (username, galleryId, imageId, nextImageId, galleryName, galleryType) => {
    GalleryService.deletePhoto(username, galleryId, imageId, nextImageId, galleryName, galleryType);
  };

  @action deleteGallery = (username, galleryId) => {
    GalleryService.deleteGallery(username, galleryId);
  };

  @action resetDeletePhotoState = () => {
    this.deletePhotoState = APIStates.INITIAL;
  };

  /**
   * Shapes the raw data for ready to display props
   *
   * @readonly
   * @memberof GalleryStore
   * @returns {Array} = Consumer ready galleries
   */
  @computed get displayGalleries() {
    return this.galleries
      .map((gallery) => {
        return {
          ...gallery,
          isFanGallery: gallery.galleryType === 'FANCLUB',
          isLocked: gallery.galleryType === 'FANCLUB' && !FanClubStore.isFan,
          mainImage: gallery.mainImage || { previewUrl: DefaultImageSrc },
        };
      })
      .filter((gallery) => gallery.imageCount > 0)
      // Make Profile gallery is the first one
      .sort((a) => {
        if (a.galleryType === 'PROFILE') {
          return -1;
        }

        return 1;
      });
  }

  /**
   * Shapes the raw data for ready to display props
   *
   * @readonly
   * @memberof GalleryStore
   * @returns {Array} = Consumer ready galleries
   */
  @computed get displayFanclubGalleries() {
    return this.galleries
      .filter((gallery) => gallery.imageCount > 0 && gallery.galleryType === ENUM_GALLERY_TYPE.FANCLUB)
      .map((gallery) => {
        return {
          isFanGallery: gallery.galleryType === ENUM_GALLERY_TYPE.FANCLUB,
          isLocked: gallery.galleryType === ENUM_GALLERY_TYPE.FANCLUB && !FanClubStore.isFan,
          ...gallery,
        };
      });
  }

  @computed get profileBannerPic() {
    return this.galleries
      .filter((gallery) => gallery.galleryType === ENUM_GALLERY_TYPE.PROFILE && gallery.imageCount > 0)
      .map((gallery) => {
        return {
          mainImageUrl: gallery.mainImage?.imageUrl,
          status: gallery.mainImage?.imageApprovalStatus,
        };
      });
  }

  /**
   * Shapes the raw data for ready to display props
   *
   * @readonly
   * @memberof GalleryStore
   * @returns {Array} = Consumer ready galleries
   */
  @computed get displayNonFanClubGalleries() {
    return this.galleries
      .filter((gallery) => gallery.galleryType !== 'FANCLUB' && gallery.imageCount > 0)
      .sort((a) => {
        if (a.galleryType === 'PROFILE') {
          return -1;
        }

        return 1;
      });
  }

  @computed get hasFanClubGalleries() {
    return this.displayFanclubGalleries.length > 0;
  }

  @computed get hasNonFanClubGalleries() {
    return this.displayNonFanClubGalleries.length > 0;
  }

  @computed get galleriesWithTimeStamps() {
    const { isFanClubActive } = FanClubStore;
    if (!isFanClubActive) {
      return this.displayNonFanClubGalleries.map((gallery) => {
        let dateAdded = undefined;
        if (gallery.lastUploaded !== null) {
          dateAdded = moment(gallery.lastUploaded).format('x');
        } else {
          dateAdded = moment(gallery.dateAdded).format('x');
        }

        return {
          ...gallery,
          dateAdded,
        };
      });
    }

    return this.displayGalleries.map((gallery) => {
      let dateAdded = undefined;
      if (gallery.lastUploaded !== null) {
        dateAdded = moment(gallery.lastUploaded).format('x');
      } else {
        dateAdded = moment(gallery.dateAdded).format('x');
      }

      return {
        ...gallery,
        dateAdded,
      };
    });

  }

  @computed get isLoadingGalleries() {
    return this.galleriesState === APIStates.PENDING;
  }

  /**
   * Checks if the performer has galleries
   *
   * @readonly
   * @memberof GalleryStore
   * @returns {boolean}
   */
  @computed get hasGalleries() {
    return this.galleries.length > 0;
  }

  @computed get lightboxPhotos() {
    return this.photos
      .map((photo) => {
        photo.src = photo.imageUrl;

        return photo;
      })
      .sort((a, b) => new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime());
  }

  /**
   * Checks if the performer has non-empty galleries
   *
   * @readonly
   * @memberof GalleryStore
   * @returns {Boolean}
   */
  @computed get hasPhotos() {
    return !!this.galleries.length && !!this.galleries.filter((gallery) => gallery.imageCount > 0).length;
  }
}

export default new GalleryStore();
